import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, Nav, Pagination, Table } from "react-bootstrap";
import { TableSkeleton } from "../../skeleton/TableSkeleton";
import { getProformaGenerations } from "../../service/api";
import { proformaHeaderFields } from "./FieldsAray";
import { showPageNumbers } from "../../utils/table";
import clsx from "clsx";
import styles from "./index.module.css";
import Select from "react-select";
import { tracker } from "../../service/mixpanel";

const paginationOptions = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
];
const ProformaGenerationsTable = ({ searchTerm, isAdded }) => {
  const [loading, setLoading] = useState(false);
  const [proformas, setProformas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const firstLoad = useRef(true);

  const fetchProformaData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProformaGenerations(
        currentPage,
        itemsPerPage,
        searchTerm
      );
      setProformas(response?.data?.data || []);
      setTotalDocs(response?.data?.totalDocs || 0);
      setTotalPages(Math.ceil((response?.data?.totalDocs || 0) / itemsPerPage));
    } catch (error) {
      console.error("Error fetching proformas:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, isAdded]);

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      fetchProformaData();
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchProformaData();
    }
    tracker.proformaGeneration.proformaSearch(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    fetchProformaData();
  }, [currentPage, itemsPerPage, isAdded]);

  return (
    <>
      {loading ? (
        <TableSkeleton />
      ) : (
        <div className="scrollable-table-container ">
          <Card
            border="light"
            className="table-wrapper table-responsive shadow-sm"
          >
            <Card.Body className="pt-0">
              <div className="table-scroll-container">
                <Table
                  hover
                  className="user-table align-items-center table-striped"
                >
                  <thead>
                    <tr>
                      {proformaHeaderFields.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {proformas.length <= 0 ? (
                      <tr>
                        <td className="bg-grey" colSpan={100}>
                          No Records Found.
                        </td>
                      </tr>
                    ) : (
                      proformas.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item?.proformaId || "-"}</td>
                          <td>{item?.proformaName || "-"}</td>
                          <td>{item?.scenarioName || "-"}</td>
                          <td>{item?.adjustedGrossIncome ?? "-"}</td>
                          <td>{item?.lossLimitation || "-"}</td>
                          <td>{item?.contactGroup || "-"}</td>
                          <td>{item?.federalRevenueSpread || "-"}</td>
                          <td>{item?.state || "-"}</td>
                          <td>{item?.federalMarginalTaxRate ?? "-"}</td>
                          <td>
                            {" "}
                            {item?.internalMarkup !== null &&
                            item?.internalMarkup !== undefined
                              ? `${item.internalMarkup}%`
                              : "-"}
                          </td>
                          <td>
                            {" "}
                            {item?.affiliateMarkup !== null &&
                            item?.affiliateMarkup !== undefined
                              ? `${item.affiliateMarkup}%`
                              : "-"}
                          </td>
                          <td>
                            {item?.internalMarkup !== null &&
                            item?.internalMarkup !== undefined &&
                            item?.affiliateMarkup !== null &&
                            item?.affiliateMarkup !== undefined
                              ? `${(
                                  item.internalMarkup + item.affiliateMarkup
                                ).toFixed(2)}%`
                              : "-"}
                          </td>

                          <td>{item?.clientName || "-"}</td>
                          <td>
                            {" "}
                            {item?.itcTarget !== null &&
                            item?.itcTarget !== undefined
                              ? `${item.itcTarget}%`
                              : "-"}
                          </td>
                          <td>{item?.proformaGDrive || "-"}</td>
                          <td>
                            {item?.affiliateMarkupEC !== null &&
                            item?.affiliateMarkupEC !== undefined
                              ? `${item.affiliateMarkupEC}%`
                              : "-"}
                          </td>
                          <td>{item?.prepaymentIncome || "-"}</td>
                          <td>{item?.federalTaxCreditAmount || "-"}</td>
                          <td>{item?.itcEcRatio || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
            </Card.Body>
            <Card.Footer
              className={clsx(
                "px-3 border-0 d-lg-flex justify-content-between",
                styles.stickyFooter
              )}
            >
              <Nav>
                <Pagination className="mb-2 mb-lg-0">
                  <Pagination.Prev
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Pagination.Prev>

                  {totalPages > 10 && (
                    <>
                      {currentPage > 4 && (
                        <>
                          <Pagination.Item onClick={() => setCurrentPage(1)}>
                            1
                          </Pagination.Item>
                          <Pagination.Ellipsis disabled />
                        </>
                      )}
                      {[...Array(totalPages)].map((_, page) => {
                        if (showPageNumbers(page, currentPage, totalPages)) {
                          return (
                            <Pagination.Item
                              key={page + 1}
                              active={page + 1 === currentPage}
                              onClick={() => setCurrentPage(page + 1)}
                            >
                              {page + 1}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      {currentPage < totalPages - 3 && (
                        <>
                          <Pagination.Ellipsis disabled />
                          <Pagination.Item
                            onClick={() => setCurrentPage(totalPages)}
                          >
                            {totalPages}
                          </Pagination.Item>
                        </>
                      )}
                    </>
                  )}

                  {/* Fallback when pages are small (<10) */}
                  {totalPages <= 10 &&
                    [...Array(totalPages)].map((_, page) => (
                      <Pagination.Item
                        key={page + 1}
                        active={page + 1 === currentPage}
                        onClick={() => setCurrentPage(page + 1)}
                      >
                        {page + 1}
                      </Pagination.Item>
                    ))}

                  <Pagination.Next
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(prev + 1, Math.ceil(totalDocs / itemsPerPage))
                      )
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </Pagination.Next>
                </Pagination>
              </Nav>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Items Per Page
                <Select
                  options={paginationOptions}
                  defaultValue={paginationOptions.find(
                    (opt) => opt.value == itemsPerPage
                  )}
                  onChange={(selected) => {
                    if (selected) {
                      console.log(selected);
                      setItemsPerPage(selected.value);
                      setCurrentPage(1);
                    }
                  }}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "32px",
                      height: "32px",
                      fontSize: "14px",
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                      padding: "0 6px",
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: "14px",
                      padding: "6px 12px",
                      minHeight: "28px",
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }),
                  }}
                  menuPlacement="top"
                />
              </div>
            </Card.Footer>
          </Card>
        </div>
      )}
    </>
  );
};

export default ProformaGenerationsTable;
